// ApiList.js - Final Complete Version
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Popconfirm, 
  message, 
  Tag, 
  Row, 
  Col, 
  Select, 
  Empty, 
  Tooltip, 
  Typography 
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  ReloadOutlined, 
  DeleteOutlined, 
  SearchOutlined, 
  FilterOutlined,
  TagsOutlined,
  ApiOutlined
} from '@ant-design/icons';
import apiClient from '../services/axiosConfig';
import debounce from 'lodash/debounce';
import { useTheme } from '../App';
import '../App.css';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

function ApiList({ onAddApi, onEditApi, refreshTrigger }) {
  const { theme, currentTheme } = useTheme();
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [groupOptions, setGroupOptions] = useState([]);
  const [methodOptions, setMethodOptions] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    groups: [],
    methods: []
  });
  const [tableKey, setTableKey] = useState(0); // Add table key for forcing re-render
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Memoized debounced search function
  const debouncedSearch = useMemo(
    () => debounce((searchValue) => {
      fetchApis({
        search: searchValue,
        page: 1, // Reset to first page on search
        pageSize: pagination.pageSize,
        group: selectedFilters.groups,
        method: selectedFilters.methods
      });
    }, 300),
    [pagination.pageSize, selectedFilters]
  );

  // Fetch APIs with proper parameters
  const fetchApis = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/config`, {
        params: {
          search: params.search !== undefined ? params.search : searchText,
          group: params.group || selectedFilters.groups,
          method: params.method || selectedFilters.methods,
          page: params.page || pagination.current,
          pageSize: params.pageSize || pagination.pageSize,
          sortBy: params.sortBy, // Remove default sort
          order: params.order
        },
        headers: {
          'Cache-Control': 'no-cache', // Bypass cache
          'Pragma': 'no-cache'
        }
      });
      
      const { data, pagination: paginationData } = response.data;
      setApis(data);
      setPagination(prev => ({
        ...prev,
        ...paginationData
      }));
    } catch (error) {
      message.error('Failed to fetch APIs');
      console.error('Error fetching APIs:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, searchText, selectedFilters, pagination.current, pagination.pageSize]);

  // Fetch filter options once
  const fetchFilterOptions = async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/config/filters`, {
        headers: {
          'Cache-Control': 'no-cache', // Bypass cache to get latest groups
          'Pragma': 'no-cache'
        }
      });
      setGroupOptions(response.data.groups || []);
      setMethodOptions(response.data.methods || []);
    } catch (error) {
      message.error('Failed to fetch filter options');
    }
  };

  // Initial load
  useEffect(() => {
    fetchFilterOptions();
    fetchApis();
  }, []);

  // Refresh data when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchFilterOptions(); // Refresh filter options to include new groups
      fetchApis(); // Refresh API list
    }
  }, [refreshTrigger]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  // Handle table changes (pagination, sorting, filtering)
  const handleTableChange = (newPagination, filters, sorter) => {
    // Apply filters from table columns - handle null/undefined filters properly
    const newGroups = filters.group === null ? [] : (filters.group || selectedFilters.groups);
    const newMethods = filters.method === null ? [] : (filters.method || selectedFilters.methods);
    
    setSelectedFilters({
      groups: newGroups,
      methods: newMethods
    });

    fetchApis({
      page: newPagination.current,
      pageSize: newPagination.pageSize,
      sortBy: sorter.field,
      order: sorter.order === 'descend' ? 'desc' : 'asc',
      group: newGroups,
      method: newMethods
    });
  };

  // Handle single delete
  const handleDelete = async (_id) => {
    try {
      await apiClient.delete(`${API_BASE_URL}/config/${_id}`);
      message.success('API deleted successfully');
      fetchApis(); // Refresh current page
    } catch (error) {
      message.error('Failed to delete API');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      await apiClient.post(`${API_BASE_URL}/config/bulk-delete`, {
        ids: selectedRowKeys
      });
      message.success(`${selectedRowKeys.length} APIs deleted successfully`);
      setSelectedRowKeys([]);
      fetchApis(); // Refresh current page
    } catch (error) {
      message.error('Failed to delete APIs');
    }
  };

  // Handle sort order update
  const handleSortOrderBlur = async (record, e) => {
    const newOrder = Number(e.target.value);
    if (isNaN(newOrder)) {
      message.error("Sort order must be a number");
      return;
    }
    try {
      await apiClient.patch(`${API_BASE_URL}/config/sort-order/${record._id}`, { 
        groupSortOrder: newOrder 
      });
      
      // Update local state optimistically
      setApis(prev =>
        prev.map(api =>
          api._id === record._id ? { ...api, groupSortOrder: newOrder } : api
        )
      );
      message.success("Sort order updated successfully");
    } catch (error) {
      message.error("Failed to update sort order");
    } finally {
      setEditingRowId(null);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchApis();
    message.success('API list refreshed');
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchText('');
    setSelectedFilters({ groups: [], methods: [] });
    
    // Reset table filters by setting table key to a new value
    setTableKey(prevKey => prevKey + 1);
    
    fetchApis({
      search: '',
      group: [],
      method: [],
      page: 1
    });
  };

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const columns = [
    {
      title: 'No',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      fixed: 'left',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: 'API Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, color: theme.text }}>{text}</div>
          </div>
          {record.tags?.length > 0 && (
            <Tooltip title={`Tags: ${record.tags.join(', ')}`}>
              <Tag 
                icon={<TagsOutlined />} 
                color="blue" 
                style={{ cursor: 'pointer' }}
              >
                {record.tags.length}
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Group',
      dataIndex: 'group',
      key: 'group',
      width: 150,
      filters: groupOptions.map(group => ({ text: group, value: group })),
      filteredValue: selectedFilters.groups,
      filterMultiple: true,
      render: (group) => (
        <Tag color="purple">{group}</Tag>
      ),
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      width: 350,
      ellipsis: true,
      render: (route) => (
        <Tooltip title={route}>
          <code style={{ 
            fontSize: '12px', 
            backgroundColor: currentTheme === 'dark' ? theme.codeBg : '#f6f8fa',
            padding: '2px 6px',
            borderRadius: '3px',
            color: currentTheme === 'dark' ? '#ff7875' : '#d4380d',
            border: `1px solid ${theme.border}`
          }}>
            {route}
          </code>
        </Tooltip>
      ),
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 100,
      filters: methodOptions.map(method => ({ text: method, value: method })),
      filteredValue: selectedFilters.methods,
      filterMultiple: true,
      render: (method) => (
        <Tag 
          color={
            method === 'GET' ? 'green' :
            method === 'POST' ? 'blue' :
            method === 'PUT' ? 'orange' :
            method === 'DELETE' ? 'red' :
            'default'
          }
          style={{ fontWeight: 500 }}
        >
          {method}
        </Tag>
      ),
    },
    {
      title: 'Sort Order',
      dataIndex: 'groupSortOrder',
      key: 'groupSortOrder',
      width: 120,
      sorter: true,
      render: (text, record) => {
        const isEditing = editingRowId === record._id;
        return isEditing ? (
          <Input
            defaultValue={text}
            style={{ width: 80 }}
            onBlur={(e) => handleSortOrderBlur(record, e)}
            onPressEnter={(e) => handleSortOrderBlur(record, e)}
            autoFocus
          />
        ) : (
          <div
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onDoubleClick={() => setEditingRowId(record._id)}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme === 'dark' ? '#2a2a2a' : '#f0f0f0'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ color: theme.text }}>{text || 0}</span>
            <EditOutlined
              onClick={() => setEditingRowId(record._id)}
              style={{ marginLeft: 8, fontSize: 12, color: theme.primary }}
            />
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEditApi(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this API?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Check if any filters are active
  const hasActiveFilters = searchText || selectedFilters.groups.length > 0 || selectedFilters.methods.length > 0;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header Actions */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          <div style={{ flex: 1, minWidth: 400, maxWidth: 700 }}>
            <Search
              placeholder="Search APIs by name, description, route or tags..."
              value={searchText}
              onChange={handleSearchChange}
              allowClear
              prefix={<SearchOutlined />}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <Space>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} selected APIs?`}
              onConfirm={handleBulkDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
          {hasActiveFilters && (
            <Button onClick={handleClearFilters} icon={<FilterOutlined />}>
              Clear Filters
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAddApi}
          >
            Add API
          </Button>
        </Space>
      </div>

      {/* Summary Stats */}
      {!loading && (
        <Row style={{ marginBottom: 16 }}>
          <Col>
            <Space size="large">
              <Text type="secondary">
                Total APIs: <Text strong>{pagination.total}</Text>
              </Text>
              {hasActiveFilters && (
                <Text type="secondary">
                  Showing: <Text strong>{apis.length}</Text> results
                </Text>
              )}
              {selectedFilters.groups.length > 0 && (
                <Text type="secondary">
                  Groups: {selectedFilters.groups.map(g => (
                    <Tag key={g} color="purple" size="small">{g}</Tag>
                  ))}
                </Text>
              )}
              {selectedFilters.methods.length > 0 && (
                <Text type="secondary">
                  Methods: {selectedFilters.methods.map(m => (
                    <Tag 
                      key={m} 
                      color={
                        m === 'GET' ? 'green' :
                        m === 'POST' ? 'blue' :
                        m === 'PUT' ? 'orange' :
                        m === 'DELETE' ? 'red' :
                        'default'
                      } 
                      size="small"
                    >
                      {m}
                    </Tag>
                  ))}
                </Text>
              )}
            </Space>
          </Col>
        </Row>
      )}

      {/* Table */}
      <Table
        className="api-list-table"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={apis}
        rowKey="_id"
        loading={loading}
        scroll={{ x: 1400 }}
        onChange={handleTableChange}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (total, range) => (
            <span style={{ color: theme.textSecondary }}>
              Showing {range[0]}-{range[1]} of {total} APIs
            </span>
          ),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                hasActiveFilters 
                  ? "No APIs found matching your filters"
                  : "No APIs created yet"
              }
            >
              {!hasActiveFilters && (
                <Button type="primary" onClick={onAddApi} icon={<PlusOutlined />}>
                  Create Your First API
                </Button>
              )}
            </Empty>
          )
        }}
        rowClassName={(record, index) => 
          index % 2 === 0 ? 'api-list-even-row' : 'api-list-odd-row'
        }
        key={tableKey}
      />
    </div>
  );
}

export default ApiList;